import { useState } from "react";
import {
  Paper,
  Group,
  Stack,
  Text,
  Button,
  Loader,
  Center,
  Combobox,
  useCombobox,
  InputBase,
  Badge,
} from "@mantine/core";
import { IconFlag, IconPlus } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  flagDefinitionsService,
  CaseFlagApplication,
} from "../../services/flagDefinitionsService";
import { FlagBadge } from "./FlagBadge";
import { useAuthStore } from "../../store/auth.store";

interface CaseFlagsSectionProps {
  caseId: string | null;
  embedded?: boolean;
}

const MAX_VISIBLE = 5;

export function CaseFlagsSection({ caseId, embedded }: CaseFlagsSectionProps) {
  const queryClient = useQueryClient();
  const profile = useAuthStore((s) => s.profile);
  const canFlag = profile?.permissions?.includes("cases.flag") ?? false;

  const [expanded, setExpanded] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const combobox = useCombobox();

  const { data: caseFlags = [], isLoading } = useQuery({
    queryKey: ["caseFlags", caseId],
    queryFn: () =>
      caseId
        ? flagDefinitionsService.getCaseFlags(caseId)
        : Promise.resolve([]),
    enabled: !!caseId,
  });

  const { data: activeDefinitions = [] } = useQuery({
    queryKey: ["activeFlagDefinitions"],
    queryFn: () => flagDefinitionsService.getActive(),
    enabled: !!caseId && canFlag,
  });

  const applyMutation = useMutation({
    mutationFn: (flagDefinitionId: string) =>
      flagDefinitionsService.applyFlag(caseId!, flagDefinitionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["caseFlags", caseId] });
    },
    onError: (err: any) => {
      notifications.show({
        title: "Error",
        message:
          err?.response?.data?.error || err.message || "Failed to apply flag",
        color: "red",
      });
    },
  });

  const removeMutation = useMutation({
    mutationFn: (applicationId: string) =>
      flagDefinitionsService.removeFlag(caseId!, applicationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["caseFlags", caseId] });
      setRemovingId(null);
    },
    onError: (err: any) => {
      notifications.show({
        title: "Error",
        message:
          err?.response?.data?.error || err.message || "Failed to remove flag",
        color: "red",
      });
      setRemovingId(null);
    },
  });

  const handleRemove = (applicationId: string) => {
    setRemovingId(applicationId);
    removeMutation.mutate(applicationId);
  };

  const appliedIds = new Set(caseFlags.map((f) => f.flagDefinitionId));
  const availableFlags = activeDefinitions.filter((d) => !appliedIds.has(d.id));

  const visibleFlags = expanded ? caseFlags : caseFlags.slice(0, MAX_VISIBLE);
  const hiddenCount = caseFlags.length - MAX_VISIBLE;

  if (!caseId) return null;

  const content = (
    <Stack gap="xs">
      {!embedded && (
        <Group gap="xs">
          <IconFlag size={16} />
          <Text fw={500} size="sm">
            Flags
          </Text>
          {isLoading && <Loader size={14} />}
        </Group>
      )}

      {isLoading && !caseFlags.length ? (
        <Center py="xs">
          <Loader size="sm" />
        </Center>
      ) : (
        <Group gap="xs" wrap="wrap">
          {visibleFlags.map((app: CaseFlagApplication) => (
            <FlagBadge
              key={app.id}
              application={app}
              onRemove={canFlag ? handleRemove : undefined}
              loading={removingId === app.id}
            />
          ))}

          {!expanded && hiddenCount > 0 && (
            <Badge
              variant="outline"
              color="gray"
              size="sm"
              style={{ cursor: "pointer" }}
              onClick={() => setExpanded(true)}
            >
              +{hiddenCount} more
            </Badge>
          )}

          {expanded && hiddenCount > 0 && (
            <Badge
              variant="outline"
              color="gray"
              size="sm"
              style={{ cursor: "pointer" }}
              onClick={() => setExpanded(false)}
            >
              Show less
            </Badge>
          )}

          {canFlag && availableFlags.length > 0 && (
            <Combobox
              store={combobox}
              onOptionSubmit={(val) => {
                applyMutation.mutate(val);
                combobox.closeDropdown();
              }}
            >
              <Combobox.Target>
                <InputBase
                  component="button"
                  type="button"
                  pointer
                  onClick={() => combobox.toggleDropdown()}
                  style={{
                    border: "none",
                    background: "none",
                    padding: 0,
                    cursor: "pointer",
                  }}
                >
                  <Button
                    variant="subtle"
                    size="compact-xs"
                    leftSection={<IconPlus size={12} />}
                    loading={applyMutation.isPending}
                  >
                    Add Flag
                  </Button>
                </InputBase>
              </Combobox.Target>

              <Combobox.Dropdown>
                <Combobox.Options>
                  {availableFlags.map((def) => (
                    <Combobox.Option key={def.id} value={def.id}>
                      <Group gap="xs">
                        <Badge color={def.color} variant="light" size="xs">
                          {def.name}
                        </Badge>
                      </Group>
                    </Combobox.Option>
                  ))}
                </Combobox.Options>
              </Combobox.Dropdown>
            </Combobox>
          )}

          {caseFlags.length === 0 && !canFlag && (
            <Text size="xs" c="dimmed">
              No flags
            </Text>
          )}
        </Group>
      )}
    </Stack>
  );

  if (embedded) return content;

  return (
    <Paper p="sm" withBorder>
      {content}
    </Paper>
  );
}
